<template>
  <SideModal
    :show="parent && show"
    @update:show="
      {
        if (!state.loadingSave) $emit('update:show', $event);
      }
    "
    location="left"
    close-on-overlay-click
  >
    <form @submit.prevent="save" class="modal-container">
      <div class="modal-top">
        <span class="d-flex gap-3 align-center">
          <Typography color="primary" weight="600" size="1.25rem">Edit Parent</Typography>
          <v-icon size="x-small" @click="copyToClipboard(parent.uid)">mdi-content-copy</v-icon>
        </span>
        <v-btn
          @click="
            {
              if (!state.loadingSave) $emit('update:show', false);
            }
          "
          variant="plain"
          color="normal"
          icon
          ><v-icon color="gray600">mdi-close</v-icon></v-btn
        >
      </div>

      <div class="modal-body">
        <template v-if="!state.passwordForm">
          <v-text-field
            :disabled="parent.deleted"
            class="custom-input"
            color="primary"
            label="First Name"
            v-model="state.firstname"
          ></v-text-field>
          <v-text-field
            :disabled="parent.deleted"
            class="custom-input"
            color="primary"
            label="Last Name"
            v-model="state.lastname"
          ></v-text-field>
          <div>
            <v-text-field
              :disabled="parent.deleted"
              class="custom-input"
              color="primary"
              label="E-Mail"
              v-model="state.email"
              required
              type="email"
            ></v-text-field>
            <FormValidate
              email
              :validate="state.email"
              title="Field must be an email"
            ></FormValidate>
          </div>
          <div>
            <v-text-field
              :disabled="parent.deleted"
              class="custom-input"
              color="primary"
              label="Phone Number"
              v-model="state.phone"
            ></v-text-field>
            <FormValidate
              :validate="state.phone"
              :regex="/^.{0,15}$/"
              title="Phone Number must be a string of 0 to 15 characters"
            ></FormValidate>
          </div>
          <v-text-field
            class="custom-input"
            color="primary"
            label="External ID"
            disabled
            :model-value="props.parent.external_id"
          ></v-text-field>
        </template>
        <template v-else>
          <password-input
            class="custom-input"
            color="primary"
            label="Password"
            v-model="state.newPassword"
            autocomplete="new-password"
          ></password-input>
        </template>
        <div>
          <div style="display: flex; width: 100%; gap: 15px; justify-content: stretch">
            <v-btn
              v-if="!parent.deleted"
              @click="setDelete(true)"
              :loading="state.loadingDelete"
              style="text-transform: capitalize; flex-grow: 1"
              color="#E5E4DB"
            >
              Delete
            </v-btn>
            <v-btn
              v-if="!parent.deleted"
              type="submit"
              :loading="state.loadingSave"
              style="text-transform: capitalize; flex-grow: 1"
              >Save</v-btn
            >
            <v-btn
              v-else
              @click="setDelete(false)"
              :loading="state.loadingDelete"
              style="text-transform: capitalize; flex-grow: 1"
              >Restore</v-btn
            >
          </div>
          <v-btn
            variant="text"
            color="gray700"
            width="max-content"
            style="text-transform: capitalize"
            density="compact"
            class="mt-2"
            @click="state.passwordForm = !state.passwordForm"
            >{{ state.passwordForm ? "Cancel" : "Reset Parent Password" }}</v-btn
          >
        </div>
      </div>
    </form>
  </SideModal>
</template>

<script setup lang="ts">
import type { GuardiansType } from "@/api_types/school";
import SideModal from "@/components/sideModal.vue";
import Typography from "@/components/typography.vue";
import passwordInput from "@/components/passwordInput.vue";
import { watch } from "vue";
import { reactive } from "vue";
import { useAlertsStore } from "@/stores/alerts";
import useParents from "@/stores/school/parents";
import FormValidate from "@/components/formValidate.vue";
import { copyToClipboard } from "@/utils";

const props = defineProps<{
  parent: GuardiansType[number];
  show: boolean;
}>();

const parentsS = useParents();

const state = reactive({
  loadingSave: false,
  loadingDelete: false,
  firstname: "",
  lastname: "",
  email: "",
  passwordForm: false,
  newPassword: "",
  phone: ""
});

watch([() => props.parent, () => props.show], () => {
  if (props.parent)
    Object.assign(state, {
      firstname: props.parent.firstname,
      lastname: props.parent.lastname,
      email: props.parent.email,
      phone: props.parent.phone
    });
});

defineEmits<{
  (e: 'update:show', ...args: any[]): void
}>();

const save = async () => {
  state.loadingSave = true;

  if (state.passwordForm) {
    // saving new password
    const res = await parentsS.resetPassword(props.parent.uid, state.newPassword);
    useAlertsStore().addAlert(
      res ? "success" : "error",
      res ? "Parent Password updated successfully" : "Failed to update parent password",
      7
    );
  } else {
    // save parent
    const res = await parentsS.updateParent(props.parent.uid, {
      firstname: state.firstname,
      lastname: state.lastname,
      email: state.email,
      phone: state.phone
    });
    useAlertsStore().addAlert(
      res ? "success" : "error",
      res ? "Parent Details updated successfully" : "Failed to update parent details",
      7
    );
  }
  state.loadingSave = false;
};
const setDelete = async (val: boolean) => {
  state.loadingDelete = true;

  if (state.passwordForm) {
    // saving new password
  } else {
    // save parent
    const res = await parentsS.updateParent(props.parent.uid, {
      deleted: val
    });
    const message = val ? "delete" : "restore";
    useAlertsStore().addAlert(
      res ? "success" : "error",
      res ? `Parent ${message}d successfully` : `Failed to ${message} parent`,
      7
    );
  }
  state.loadingDelete = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-container {
  padding: 30px;

  .modal-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .modal-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 30px;
  }
}
</style>
